import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  Plus,
  Radar,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRegions } from '../../hooks/useRegions';
import { useDivisions } from '../../hooks/useDivisions';
import { useGatewayClaim } from '../../hooks/useGatewayClaim';
import { claimDetectedGateway, type AvailableGatewayInfo } from '../../services/gatewayClaim';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { cn } from '@/lib/utils';

const NO_DIVISION = '__none__';

/** How long to wait before pointing out that the browser prompt is waiting. */
const PROMPT_NUDGE_MS = 5_000;
/** How long a scan runs before we start suggesting why it might be empty. */
const NOTHING_FOUND_HINT_MS = 25_000;

/**
 * Recovery steps differ per browser and are useless if vague, so name the
 * actual clicks. Chrome-family exposes a per-site toggle; Safari's is an
 * OS-level switch that governs the whole browser.
 */
function blockedRecoverySteps(): string {
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

  if (isSafari) {
    return 'Open System Settings → Privacy & Security → Local Network and turn on the switch for Safari, then scan again.';
  }
  return 'Click the icon to the left of the address bar → Site settings → Local network access → Allow, then scan again.';
}

interface Props {
  /**
   * `hero` is the full explainer, for when the org has no gateways and this is
   * the main thing to do on the page. `compact` is a single strip for when it
   * is a secondary action above an existing table.
   */
  variant?: 'hero' | 'compact';
}

/**
 * The "find a gateway on this network" surface.
 *
 * Owns the four permission states (ready / scanning / blocked / found) plus the
 * claim form. The beacon itself lives in the app-wide provider — this panel
 * arms it on mount and boosts it to a 5s cadence while the user waits.
 */
export function LocalNetworkScanPanel({ variant = 'hero' }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const {
    claimCode,
    permission,
    discoveredGateways,
    isForegroundScanning,
    isWatching,
    isConsentWatch,
    stopWatching,
    scanTimedOut,
    scanElapsedMs,
    error,
    sessionError,
    arm,
    startScan,
    stopScan,
  } = useGatewayClaim();

  // Expanding the explainer from the compact strip: the browser prompt should
  // never arrive before the sentence explaining why we want it.
  const [explainerOpen, setExplainerOpen] = useState(false);

  const { data: regions, isLoading: isLoadingRegions } = useRegions(organizationId);
  const { data: divisions, isLoading: isLoadingDivisions } = useDivisions(organizationId);

  const [claimingGateway, setClaimingGateway] = useState<AvailableGatewayInfo | null>(null);
  const [claimName, setClaimName] = useState('');
  const [claimRegionId, setClaimRegionId] = useState('');
  const [claimDivisionId, setClaimDivisionId] = useState(NO_DIVISION);

  // Pre-register the claim session so the Scan click can beacon immediately.
  useEffect(() => { arm(); }, [arm]);

  const claimMutation = useMutation<unknown, Error, AvailableGatewayInfo>({
    mutationFn: async (gateway) => {
      if (!claimName || !claimRegionId) {
        throw new Error('Gateway name and region are required');
      }
      await claimDetectedGateway(
        claimCode,
        gateway.sessionId,
        claimName,
        claimRegionId,
        claimDivisionId === NO_DIVISION ? undefined : claimDivisionId,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      void queryClient.invalidateQueries({ queryKey: ['available-gateways'] });
      closeClaimModal();
    },
  });

  const openClaimModal = (gateway: AvailableGatewayInfo) => {
    setClaimingGateway(gateway);
    setClaimName(`Gateway ${gateway.serial}`);
    setClaimRegionId(regions && regions.length === 1 ? regions[0].id : '');
    setClaimDivisionId(NO_DIVISION);
    claimMutation.reset();
  };

  const closeClaimModal = () => {
    setClaimingGateway(null);
    setClaimName('');
    setClaimRegionId('');
    setClaimDivisionId(NO_DIVISION);
    claimMutation.reset();
  };

  const handleClaimSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (claimingGateway) claimMutation.mutate(claimingGateway);
  };

  const handleScan = () => {
    setExplainerOpen(false);
    startScan();
  };

  const hasResults = discoveredGateways.length > 0;
  const isBlocked = permission === 'denied';
  /**
   * Only Chrome-family browsers actually gate this today. Firefox and Safari
   * reject the permission query outright, so promising them a browser prompt
   * describes something that will never happen.
   */
  const browserWillPrompt = permission === 'prompt';

  const columns: readonly ResponsiveTableColumn<AvailableGatewayInfo>[] = [
    {
      key: 'serial',
      label: 'Serial number',
      render: (g) => <span className="font-medium">{g.serial}</span>,
    },
    {
      key: 'version',
      label: 'Version',
      render: (g) => (
        <Badge variant="secondary" className="font-normal">{g.softwareVersion}</Badge>
      ),
    },
    {
      key: 'ip',
      label: 'IP address',
      render: (g) => <code className="font-mono text-xs">{g.localIp}</code>,
    },
    {
      key: 'actions',
      label: '',
      hideMobileLabel: true,
      render: (g) => (
        <Button size="sm" onClick={() => { openClaimModal(g); }} className="gap-1.5">
          <Plus className="w-4 h-4" />
          Claim
        </Button>
      ),
    },
  ];

  const explainer = (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Your browser can look for unclaimed gateways on the network this computer is
        connected to.
      </p>
      {browserWillPrompt ? (
        <p className="text-sm text-muted-foreground">
          When you start, <strong className="text-foreground">your browser will ask for
          permission to access your local network</strong>. We use it for one thing:
          broadcasting a random, single-use code that nearby gateways listen for. Nothing
          is read from your network.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          We do it by broadcasting a random, single-use code that nearby gateways listen
          for. Nothing is read from your network.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        {browserWillPrompt
          ? 'Allow it once and you won’t have to come back here — we’ll let you know whenever a new gateway shows up on a network you’re on.'
          : 'Scan once and you won’t have to come back here — we’ll keep an eye out and let you know whenever a new gateway shows up on a network you’re on.'}
      </p>
    </div>
  );

  const body = () => {
    if (permission === 'checking') {
      return (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Checking…
        </p>
      );
    }

    if (isBlocked) {
      return (
        <div className="space-y-3">
          <Alert className="border-sev-2/40 bg-sev-2/10">
            <ShieldAlert className="h-4 w-4 text-sev-2" />
            <AlertDescription>
              <strong>Local network access is blocked for this site.</strong>{' '}
              {blockedRecoverySteps()}
            </AlertDescription>
          </Alert>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleScan} className="gap-1.5">
              <Search className="w-4 h-4" />
              Scan again
            </Button>
          </div>
        </div>
      );
    }

    if (isForegroundScanning) {
      const elapsed = Math.floor((scanElapsedMs ?? 0) / 1000);
      const waitingOnPrompt =
        permission === 'prompt' && (scanElapsedMs ?? 0) > PROMPT_NUDGE_MS;
      const looksEmpty =
        !hasResults && (scanElapsedMs ?? 0) > NOTHING_FOUND_HINT_MS;

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-sev-5" />
              Listening for gateways…
              <span className="text-muted-foreground tabular-nums">
                {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
              </span>
            </p>
            <Button variant="outline" size="sm" onClick={stopScan}>Stop</Button>
          </div>

          {waitingOnPrompt ? (
            <Alert className="border-sev-4/40 bg-sev-4/10">
              <AlertCircle className="h-4 w-4 text-sev-4" />
              <AlertDescription>
                Waiting on your browser — look for the permission prompt near the address
                bar and choose <strong>Allow</strong>.
              </AlertDescription>
            </Alert>
          ) : null}

          {looksEmpty ? (
            <p className="text-xs text-muted-foreground">
              Nothing yet. Check that the gateway is powered on and on the same network as
              this computer — discovery can&apos;t cross VLANs or a VPN.
            </p>
          ) : null}
        </div>
      );
    }

    if (scanTimedOut && !hasResults) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            No gateways found on this network. Check that the gateway is powered on and
            plugged into the same network as this computer.
          </p>
          <Button variant="outline" size="sm" onClick={handleScan} className="gap-1.5">
            <Search className="w-4 h-4" />
            Scan again
          </Button>
        </div>
      );
    }

    if (isWatching) {
      return (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Radar className="w-4 h-4 text-sev-4" />
            Watching this network — new gateways will appear here automatically.
          </p>
          <div className="flex items-center gap-2">
            {/* When the watch runs off a remembered click rather than a browser
                permission, there's no site-settings toggle to turn it off with,
                so we have to provide one. */}
            {isConsentWatch ? (
              <Button variant="ghost" size="sm" onClick={stopWatching}>
                Stop watching
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={handleScan} className="gap-1.5">
              <Search className="w-4 h-4" />
              Scan now
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {explainer}
        <Button onClick={handleScan} className="gap-1.5">
          <Search className="w-4 h-4" />
          Scan this network
        </Button>
      </div>
    );
  };

  // Compact: a single strip above an existing table. Expands to the full
  // explainer before it can raise a permission prompt.
  const isCompactCollapsed = variant === 'compact' && !explainerOpen && !isForegroundScanning && !hasResults;

  return (
    <>
      <section
        className={cn(
          'mb-6 rounded-lg border bg-card text-card-foreground shadow-sm',
          hasResults ? 'border-sev-4/40' : 'border-border',
        )}
      >
        {isCompactCollapsed && !isWatching && !isBlocked ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
            <p className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              Adding another gateway that&apos;s already plugged in nearby?
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setExplainerOpen(true); }}
              className="gap-1.5"
            >
              Scan this network
            </Button>
          </div>
        ) : (
          <>
            <header className="flex flex-row items-center justify-between gap-3 px-6 py-4 border-b border-border-subtle">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Search className="w-4 h-4 text-muted-foreground" />
                Find gateways on this network
              </h3>
            </header>
            <div className="px-6 py-4 space-y-4">
              {sessionError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Couldn&apos;t start discovery:</strong> {sessionError.message}
                  </AlertDescription>
                </Alert>
              ) : null}

              {error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Discovery error:</strong>{' '}
                    {error instanceof Error ? error.message : 'Unknown error'}
                  </AlertDescription>
                </Alert>
              ) : null}

              {body()}

              {hasResults ? (
                <div className="space-y-3">
                  <Alert className="border-sev-4/40 bg-sev-4/10">
                    <CheckCircle2 className="h-4 w-4 text-sev-4" />
                    <AlertDescription>
                      Found {discoveredGateways.length} unclaimed gateway
                      {discoveredGateways.length !== 1 ? 's' : ''}
                    </AlertDescription>
                  </Alert>
                  <ResponsiveTable<AvailableGatewayInfo>
                    columns={columns}
                    rows={discoveredGateways}
                    rowKey={(r) => r.sessionId}
                  />
                </div>
              ) : null}
            </div>
          </>
        )}
      </section>

      <AppSheet
        open={!!claimingGateway}
        onOpenChange={(open) => { if (!open) closeClaimModal(); }}
        title="Claim gateway"
        size="md"
      >
        <AppSheet.Body>
          {claimingGateway ? (
            <>
              <div className="rounded-md border border-sev-5/30 bg-sev-5/5 p-3 text-sm space-y-1 mb-4">
                <div><strong>Hardware ID:</strong> {claimingGateway.hardwareId}</div>
                <div><strong>Serial:</strong> {claimingGateway.serial}</div>
                <div><strong>Version:</strong> {claimingGateway.softwareVersion}</div>
                <div>
                  <strong>IP:</strong>{' '}
                  <code className="font-mono">{claimingGateway.localIp}</code>
                </div>
              </div>

              {claimMutation.isError ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Claim failed:</strong> {claimMutation.error.message}
                  </AlertDescription>
                </Alert>
              ) : null}

              <form id="claim-gateway-form" onSubmit={handleClaimSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cg-name">Gateway name</Label>
                  <Input
                    id="cg-name"
                    type="text"
                    placeholder="e.g., Main Office Gateway"
                    value={claimName}
                    onChange={(e) => { setClaimName(e.target.value); }}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cg-region">Region</Label>
                  {isLoadingRegions ? (
                    <Select disabled>
                      <SelectTrigger id="cg-region">
                        <SelectValue placeholder="Loading regions…" />
                      </SelectTrigger>
                    </Select>
                  ) : (
                    <Select value={claimRegionId} onValueChange={setClaimRegionId} required>
                      <SelectTrigger id="cg-region">
                        <SelectValue placeholder="Select a region" />
                      </SelectTrigger>
                      <SelectContent>
                        {regions?.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-xs text-muted-foreground">
                    The gateway will be assigned to this region.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="cg-division">Division (optional)</Label>
                  {isLoadingDivisions ? (
                    <Select disabled>
                      <SelectTrigger id="cg-division">
                        <SelectValue placeholder="Loading divisions…" />
                      </SelectTrigger>
                    </Select>
                  ) : (
                    <Select value={claimDivisionId} onValueChange={setClaimDivisionId}>
                      <SelectTrigger id="cg-division">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_DIVISION}>None</SelectItem>
                        {divisions?.map((division) => (
                          <SelectItem key={division.id} value={division.id}>
                            {division.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </form>
            </>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={closeClaimModal}
            disabled={claimMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="claim-gateway-form"
            disabled={claimMutation.isPending || !claimName || !claimRegionId}
            className="gap-1.5"
          >
            <Check className="w-4 h-4" />
            {claimMutation.isPending ? 'Claiming…' : 'Claim gateway'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </>
  );
}

export default LocalNetworkScanPanel;
